import { EVM_CHAINS, NATIVE_CURRENCY_CHECK_LIST } from "chainConfigs";
import { usePortfolioContext } from "contexts/PortfolioContext";
import { useTxnSign } from "hooks/useTxnSign";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useState } from "react";
import axiosService from "services/axios";
import useToastStore from "store/toast-store/useToastStore";
import useTradeStore from "store/trade-store.ts/useTradeStore";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import { checkAddressChain, roundWithDecimals } from "lib/utils";
import { CHAIN_DECIMAL, SOLANA, TON } from "consts";
import { erc20Abi } from "viem";

const withSendTokenDrawer = (Component: React.ComponentType<any>) => {
  return ({ data, isOpen, close }: { data: any[]; isOpen: boolean; close: () => void }) => {
    const {
      signAndBroadcastTonTxn,
      signAndBroadcastSolanaTxn,
      signAndBroadcastTxn,
      signAndWriteContract,
      estimateEvmGas,
    } = useTxnSign();
    const { walletAddresses, isCustodial } = useUserStoreV2();
    const { showToast } = useToastStore();
    const { refetchUserAllPortfolioAssets } = usePortfolioContext();

    const sendingRef = useRef({ isSending: false });
    const [isSending, setIsSending] = useState(false);

    const [selectedTokenIndex, setSelectedTokenIndex] = useState(0);

    const [memo, setMemo] = useState("");
    const [address, setAddress] = useState("");
    const [isValidAddress, setIsValidAddress] = useState(false);

    const [amount, setAmount] = useState(0);
    const amountRef = useRef<HTMLInputElement>(null);

    const [isOpenSearchDrawer, setIsOpenSearchDrawer] = useState(false);
    const [isOpenConfirmDrawer, setIsOpenConfirmDrawer] = useState(false);

    const { isLoading } = useTradeStore();
    const [isContinueDisabled, setIsContinueDisabled] = useState(true);

    const indexedData = useMemo(() => {
      return data
        .sort((a, b) => {
          return b.priceUsd - a.priceUsd;
        })
        .map((item, index) => {
          return {
            index,
            ...item,
          };
        });
    }, [data]);

    const handleOpenSearchDrawer = () => {
      setIsOpenSearchDrawer(true);
    };

    const handleCloseSearchDrawer = () => {
      setIsOpenSearchDrawer(false);
    };

    const handleEnterAddress = useCallback(
      (address: string) => {
        const chain = checkAddressChain(address);
        const selectedChain = EVM_CHAINS.includes(indexedData[selectedTokenIndex].chain)
          ? "evm"
          : indexedData[selectedTokenIndex].chain;

        if (chain === selectedChain) {
          setIsValidAddress(true);
          setAddress(address);
        } else {
          setIsValidAddress(false);
          setAddress(address);
        }
      },
      [indexedData, selectedTokenIndex],
    );

    const handleEnterMemo = (memo: string) => {
      setMemo(memo);
    };

    const handleMaxAmount = () => {
      if (amountRef.current && indexedData[selectedTokenIndex]) {
        const tokenUsdBalance = indexedData[selectedTokenIndex].priceUsd;

        let clampedAmount = tokenUsdBalance;

        amountRef.current.value = roundWithDecimals("floor", clampedAmount, 2).toString();
        setAmount(Number(clampedAmount));
      }
    };

    const handleOpenConfirmDrawer = () => {
      setIsOpenConfirmDrawer(true);
    };

    const handleCloseConfirmDrawer = () => {
      setIsSending(false);
      setIsOpenConfirmDrawer(false);
    };

    const handleConfirm = async () => {
      if (sendingRef.current.isSending || !indexedData[selectedTokenIndex]) {
        return;
      }

      let res;
      try {
        showToast({
          variant: "info",
          message: "Processing now. Please wait.",
        });

        handleCloseConfirmDrawer();
        close();

        const chain = indexedData[selectedTokenIndex].chain;

        // calculate the decimal amount from USD amount
        const amountInDecimal =
          (indexedData[selectedTokenIndex].balance / indexedData[selectedTokenIndex].priceUsd) *
          amount;

        if (NATIVE_CURRENCY_CHECK_LIST.includes(indexedData[selectedTokenIndex].symbol)) {
          const isMax = amount >= indexedData[selectedTokenIndex]?.priceUsd;

          let boc: string | undefined;
          let hash: string | undefined;
          let signedTxn: string[] | undefined;

          if (!isCustodial) {
            if (chain === TON) {
              boc = await signAndBroadcastTonTxn(address, amount, memo, isMax);
            } else if (chain === SOLANA && walletAddresses?.solAddress) {
              const txnMessage = await axiosService.getTxnMessage({
                chain,
                amount: amountInDecimal,
                receiverAddress: address,
                isMax,
                isTransfer: true,
              });
              signedTxn = await signAndBroadcastSolanaTxn(walletAddresses?.solAddress, txnMessage);
            } else if (walletAddresses?.ethAddress) {
              const calldatas = {
                from: walletAddresses?.ethAddress,
                to: address,
              };

              const { gas, maxFeePerGas } = await estimateEvmGas(chain, calldatas);
              const value = (
                amountInDecimal * CHAIN_DECIMAL.ethereum -
                Number(maxFeePerGas) * Number(gas)
              ).toString();

              try {
                hash = await signAndBroadcastTxn(walletAddresses?.ethAddress, chain, {
                  calldatas,
                  value,
                });
              } catch (error) {
                console.error("Error while sending token", error);
                return showToast({
                  variant: "error",
                  message: "Insufficient balance",
                  duration: 5000,
                });
              }
            }
          }

          if (isCustodial || boc || signedTxn || hash) {
            res = await axiosService.sendToken({
              receiverAddress: address,
              amount: amountInDecimal,
              signedTxn,
              chain,
              hash,
              boc,
              isMax,
            });
          }
        } else {
          let signedTxn: string[] | undefined;
          let hash: string | undefined;
          const tokenAddress = indexedData[selectedTokenIndex].tokenAddress;
          const isMax = amount >= indexedData[selectedTokenIndex]?.priceUsd;

          if (!isCustodial) {
            if (chain === SOLANA && walletAddresses?.solAddress) {
              let txnMessage: any = await axiosService.getTxnMessage({
                chain,
                amount: amountInDecimal,
                tokenAddress,
                receiverAddress: address,
                isMax,
              });

              if (txnMessage.isNewTokenAcc) {
                signedTxn = await signAndBroadcastSolanaTxn(
                  walletAddresses?.solAddress,
                  txnMessage.versionedTransaction,
                );

                const res = await axiosService.sendToken({
                  receiverAddress: address,
                  signedTxn,
                  amount: amountInDecimal,
                  chain,
                  isMax,
                });

                if (res.status < 300) {
                  txnMessage = await axiosService.getTxnMessage({
                    chain,
                    amount: amountInDecimal,
                    tokenAddress,
                    receiverAddress: address,
                    isMax,
                  });
                }
              }

              signedTxn = await signAndBroadcastSolanaTxn(walletAddresses?.solAddress, txnMessage);
            } else if (walletAddresses?.ethAddress) {
              const data: any = await axiosService.getTxnMessage({
                chain,
                amount: amountInDecimal,
                tokenAddress,
                receiverAddress: address,
              });
              if (data.value) {
                const calldata = {
                  address: tokenAddress,
                  abi: erc20Abi,
                  functionName: "transfer",
                  args: [address, Number(data.value)],
                };
                hash = await signAndWriteContract(chain, walletAddresses?.ethAddress, calldata);
              }
            }
          }

          res = await axiosService.sendToken({
            receiverAddress: address,
            chain: indexedData[selectedTokenIndex].chain,
            amount: amountInDecimal,
            tokenAddress,
            signedTxn,
            hash,
            isMax,
          });
        }

        if (res.status < 300) {
          showToast({
            variant: "success",
            message: `${res.message}`,
            duration: 5000,
          });
        } else {
          showToast({
            variant: "error",
            message: res.message,
            duration: 5000,
          });
        }
      } catch (error) {
        showToast({
          variant: "error",
          message: res ? res.message : error,
          duration: 5000,
        });
        console.error("Error while sending token", error);
      } finally {
        setIsSending(false);
        refetchUserAllPortfolioAssets();
      }
    };

    const handleSelectTokenIndex = (index: number) => {
      setSelectedTokenIndex(index);
    };

    const handleOnInputChange = () => {
      if (amountRef.current?.value !== null) {
        if (amountRef.current) {
          setAmount(parseFloat(amountRef.current.value));
        }
      }
    };

    const handleOnBlur = () => {
      if (amountRef.current?.value !== null) {
        if (amountRef.current) {
          amountRef.current.value = Number(
            Number(parseFloat(amountRef.current.value)).toFixed(2),
          ).toString();
          setAmount(Number(Number(parseFloat(amountRef.current.value)).toFixed(2)));
        }
      }
    };

    const closeSearchDrawer = (token: any) => {
      setSelectedTokenIndex(token.index);
      handleCloseSearchDrawer();
    };

    useEffect(() => {
      if (amountRef.current) {
        amountRef.current?.focus();
        amountRef.current.value = "0";
        handleEnterAddress(address);
        setAmount(0);
        handleEnterMemo("");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const isContinueDisabled = !isValidAddress || amount <= 0;
      setIsContinueDisabled(() => isContinueDisabled);
    }, [isValidAddress, amount]);

    return (
      <Component
        isOpen={isOpen}
        isLoading={isLoading}
        isSending={isSending}
        isOpenConfirmDrawer={isOpenConfirmDrawer}
        close={close}
        indexedData={indexedData}
        selectedTokenIndex={selectedTokenIndex}
        handleMaxAmount={handleMaxAmount}
        handleOpenSearchDrawer={handleOpenSearchDrawer}
        handleSelectTokenIndex={handleSelectTokenIndex}
        amount={amount}
        setAmount={setAmount}
        amountRef={amountRef}
        address={address}
        handleEnterAddress={handleEnterAddress}
        handleEnterMemo={handleEnterMemo}
        handleOpenConfirmDrawer={handleOpenConfirmDrawer}
        handleCloseConfirmDrawer={handleCloseConfirmDrawer}
        isContinueDisabled={isContinueDisabled}
        isOpenSearchDrawer={isOpenSearchDrawer}
        handleCloseSearchDrawer={handleCloseSearchDrawer}
        closeSearchDrawer={closeSearchDrawer}
        handleConfirm={handleConfirm}
        handleOnInputChange={handleOnInputChange}
        handleOnBlur={handleOnBlur}
      />
    );
  };
};

export { withSendTokenDrawer };
